import React from 'react';
import { Car, CheckCircle2, XCircle, AlertTriangle, ShieldCheck, HeartHandshake, Fuel, ArrowRight, ExternalLink, Sparkles, MessageCircle, Phone, Mail } from 'lucide-react';
import { EASY_WAY_CONTACT } from '../data/contactInfo';
import { useLanguage } from '../context/LanguageContext';

export const EasyWayPromo: React.FC = () => {
  const { lang, easyWayCancunAirportUrl, easyWayFleetUrl, easyWayWhatsAppUrl, t } = useLanguage();

  return (
    <section className="py-16 bg-[#F7F3EA] border-b border-[#ede6d8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-[#044e45]/10 text-[#044e45] mb-3 border border-[#044e45]/20">
            <Sparkles className="w-3.5 h-3.5 text-[#0F766E]" />
            {t.promo.badge}
          </div>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-[#073B4C] font-['Outfit',sans-serif]">
            {t.promo.title}
          </h2>
          <p className="mt-3 text-base text-[#4A5A5F] leading-relaxed">
            {t.promo.subtitle}
          </p>
        </div>

        {/* Comparison Grid: Taxis vs Easy Way */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Card 1: Taxis & Excursiones Rígidas (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-2xl p-6 sm:p-8 border border-[#ede6d8] flex flex-col justify-between shadow-sm">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#A8471A]/10 text-[#A8471A] flex items-center justify-center font-bold shrink-0 border border-[#A8471A]/30">
                  <XCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#172A32] leading-tight">
                    {t.promo.taxiTitle}
                  </h3>
                  <p className="text-xs text-[#A8471A] font-semibold mt-0.5">{t.promo.taxiSubtitle}</p>
                </div>
              </div>

              <ul className="space-y-3.5 text-xs sm:text-sm text-[#4A5A5F]">
                <li className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-[#A8471A] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-[#172A32]">{t.promo.taxiPoint1Title}:</strong> {t.promo.taxiPoint1Desc}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-[#A8471A] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-[#172A32]">{t.promo.taxiPoint2Title}:</strong> {t.promo.taxiPoint2Desc}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-[#A8471A] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-[#172A32]">{t.promo.taxiPoint3Title}:</strong> {t.promo.taxiPoint3Desc}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 text-[#A8471A] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-[#172A32]">{t.promo.taxiPoint4Title}:</strong> {t.promo.taxiPoint4Desc}
                  </span>
                </li>
              </ul>
            </div>

            <div className="mt-6 pt-4 border-t border-[#ede6d8] text-xs text-[#4A5A5F] font-medium">
              ❌ {t.promo.taxiExpenseLabel}{' '}
              <span className="font-extrabold text-sm text-[#A8471A] block sm:inline mt-1 sm:mt-0">{t.promo.taxiExpenseValue}</span>
            </div>
          </div>

          {/* Card 2: Autonomía con Easy Way (7 cols) */}
          <div className="lg:col-span-7 bg-gradient-to-br from-[#073B4C] via-[#052a37] to-[#04202a] text-white rounded-2xl p-6 sm:p-8 border border-[#0d4e64] shadow-xl flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-[#0F766E]/10 rounded-bl-full pointer-events-none" />

            <div>
              <div className="flex items-center justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#0F766E] text-white flex items-center justify-center font-extrabold shadow-md shrink-0">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-extrabold text-white font-['Outfit',sans-serif]">
                      {t.promo.easyWayTitle}
                    </h3>
                    <p className="text-xs text-[#2DD4BF] font-semibold mt-0.5">
                      {t.promo.easyWaySubtitle}
                    </p>
                  </div>
                </div>
                <span className="hidden sm:inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-[#D6A84B]/20 text-[#D6A84B] border border-[#D6A84B]/40">
                  {t.promo.winnerBadge}
                </span>
              </div>

              <ul className="space-y-3.5 text-xs sm:text-sm text-[#F7F3EA]/90">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-white">{t.promo.savingsPointTitle}:</strong> {t.promo.savingsPointDesc}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-white">{t.promo.spontaneousPointTitle}:</strong> {t.promo.spontaneousPointDesc}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-white">{t.promo.zeroSurprisesTitle}:</strong> {t.promo.zeroSurprisesDesc}
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#2DD4BF] shrink-0 mt-0.5" />
                  <span>
                    <strong className="text-white">{t.promo.comfortTitle}:</strong> {t.promo.comfortDesc}
                  </span>
                </li>
              </ul>
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 space-y-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-xs text-[#F7F3EA]/80 font-medium text-center sm:text-left">
                  ✓ {t.promo.investmentLabel}{' '}
                  <span className="font-extrabold text-base text-[#D6A84B] block sm:inline mt-0.5 sm:mt-0">{t.promo.investmentValue}</span>
                </div>
                <a
                  href={easyWayCancunAirportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider text-white bg-[#0F766E] hover:bg-[#0d695d] shadow-md shadow-[#0F766E]/20 transition-all active:scale-95"
                >
                  <span>{t.promo.bookBtn}</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Instant WhatsApp & Phone Row */}
              <div className="pt-3 border-t border-white/10 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="text-white/60 font-medium">{lang === 'en' ? 'Direct contact:' : lang === 'pt' ? 'Atención inmediata:' : 'Atención inmediata:'}</span>
                <a
                  href={easyWayWhatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-bold text-[#2DD4BF] hover:text-[#D6A84B] bg-white/5 hover:bg-white/10 px-2.5 py-1 rounded-lg border border-white/10 transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5 fill-current text-[#2DD4BF]" />
                  <span>WhatsApp: {EASY_WAY_CONTACT.whatsapp.display}</span>
                </a>
                <a
                  href={EASY_WAY_CONTACT.phoneMexico.link}
                  className="inline-flex items-center gap-1 text-white/70 hover:text-white bg-white/5 hover:bg-white/10 px-2.5 py-1 rounded-lg border border-white/10 transition-colors"
                >
                  <Phone className="w-3 h-3 text-[#D6A84B]" />
                  <span>Mex: {EASY_WAY_CONTACT.phoneMexico.display}</span>
                </a>
                <a
                  href={EASY_WAY_CONTACT.phoneUsa.link}
                  className="inline-flex items-center gap-1 text-white/70 hover:text-white bg-white/5 hover:bg-white/10 px-2.5 py-1 rounded-lg border border-white/10 transition-colors"
                >
                  <Phone className="w-3 h-3 text-[#D6A84B]" />
                  <span>USA / CAN: {EASY_WAY_CONTACT.phoneUsa.display}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Reassurance Banner */}
        <div className="mt-12 bg-[#172A32] text-white rounded-2xl p-6 sm:p-8 flex flex-col lg:flex-row items-center justify-between gap-6 shadow-xl border border-[#2a4754]">
          <div className="space-y-1.5 text-center lg:text-left">
            <span className="text-[11px] uppercase font-bold tracking-widest text-[#D6A84B]">
              {t.promo.reassuranceBadge}
            </span>
            <h3 className="text-lg sm:text-xl font-bold font-['Outfit',sans-serif]">
              {t.promo.reassuranceTitle}
            </h3>
            <p className="text-xs sm:text-sm text-[#F7F3EA]/70 max-w-2xl">
              {t.promo.reassuranceDesc}
            </p>
          </div>
          <div className="shrink-0">
            <a
              href={easyWayFleetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-bold text-xs uppercase tracking-wider text-white bg-[#0F766E] hover:bg-[#0d695d] shadow-lg shadow-[#0F766E]/20 transition-all"
            >
              <span>{t.promo.exploreFleetBtn}</span>
              <ArrowRight className="w-4 h-4 text-white" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
